// ui/primitives.jsx — 컴포넌트 계약(design-system-v5 §7)의 구현.
//
// 여기서 지키는 것 중 QA v2 반려와 직결된 것:
//
//   D-1  「모든 주요 폼이 첫 클릭을 삼키고, 키보드(Enter)로는 제출 자체가 불가」
//        → `<Form>` 은 진짜 `<form onSubmit>` 이고 `<Button kind="submit">` 은 진짜
//          `type="submit"` 이다. 제출 경로가 **하나**라서 클릭과 Enter 가 갈릴 수 없다.
//          div+onClick 으로 폼을 만드는 길을 아예 두지 않았다.
//
//   D-5  「360px 에서 제목이 1px 로 접히고 버튼이 82% 잘린다」
//        → 컨트롤은 `min-width: 0` 과 함께 flex 안에서 줄어들되, 라벨은 `white-space: nowrap`
//          이고 컨테이너가 `flex-wrap` 한다. 잘리는 대신 줄이 바뀐다.

import { cloneElement, toChildArray } from 'preact';
import { createPortal, forwardRef } from 'preact/compat';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { applyAutoGrow, maxHeightForRows } from './autogrow.js';
import { focusableWithin, initialFocus, trapTarget } from './focus.js';
import { useWriteGate } from '../api/write-boundary.js';
import './primitives.css';

// ── Button ────────────────────────────────────────────────────────────────

/**
 * @param tone    'neutral' | 'human' | 'danger' | 'ghost'
 * @param size    'xs' | 'sm' | 'md' | 'lg'   — 한 행 안에서는 하나만 쓴다
 * @param kind    'button' | 'submit'         — submit 은 폼 안에서만
 *
 * `tone="human"` solid 는 **뷰당 1개**다(사람 직접 판단 gate). 그 예산은 화면이 지킨다.
 */
export const Button = forwardRef(function Button(
  { tone = 'neutral', size = 'md', kind = 'button', busy = false, disabled, children, ...rest },
  ref,
) {
  const canWrite = useWriteGate();
  return (
    <button
      ref={ref}
      type={kind}
      class={`btn btn--${tone} btn--${size}`}
      disabled={disabled || busy || (kind === 'submit' && !canWrite())}
      aria-busy={busy ? 'true' : undefined}
      {...rest}
    >
      <span class="btn__label">{children}</span>
    </button>
  );
});

/**
 * 화면 상태를 바꾸지 않고 다른 작업면으로 가는 행위는 버튼이 아니라 링크다.
 * Wouter Link를 한곳에서 Button 물성과 결합해, 새 탭·브라우저 뒤로가기·주소 복사가
 * 모든 작업면에서 같은 방식으로 동작하게 한다.
 */
export function ButtonLink({ tone = 'neutral', size = 'md', children, ...rest }) {
  return (
    <Link class={`btn btn--${tone} btn--${size}`} {...rest}>
      <span class="btn__label">{children}</span>
    </Link>
  );
}

// ── Form ──────────────────────────────────────────────────────────────────

/**
 * 제출 경로는 이것 하나다.
 *
 * `onSubmit(event)` 는 **항상** `preventDefault` 된 뒤 호출된다. 호출자는 그 사실을 다시
 * 신경 쓸 필요가 없고, 잊어서 페이지가 새로고침되는 일도 없다.
 *
 * `busy` 중에는 제출을 삼킨다 — 같은 행위가 두 번 나가면 멱등 키가 소모된다.
 */
export function Form({ onSubmit, busy = false, children, ...rest }) {
  const canWrite = useWriteGate();
  return (
    <form
      novalidate
      onSubmit={(event) => {
        event.preventDefault();
        if (busy || !canWrite()) return;
        onSubmit?.(event);
      }}
      {...rest}
    >
      {children}
    </form>
  );
}

/**
 * 라벨과 입력을 잇는다. 라벨 없는 입력을 만들 수 없게 하는 것이 목적이다.
 *
 * 라벨만으로는 부족하다: 여기 적는 설명과 오류는 **그 입력에 묶여야** 한다. 안 그러면
 * 스크린 리더로 칸을 옮겨 다니는 사람에게 「commit 은 전체 해시여야 합니다」 같은 문장은
 * 존재하지 않는 것과 같고, 제출이 거절된 이유도 칸에서 들리지 않는다. 그래서 설명·오류에
 * id 를 주고 **자식 컨트롤에 `aria-describedby` 를 직접 붙인다** — 호출자가 매번
 * 기억해야 하는 일로 남기지 않는다.
 */
export function Field({ label, hint, error, htmlFor, required = false, describedBy, children }) {
  const auto = useId();
  const base = htmlFor ?? auto;
  const hintId = hint ? `${base}-hint` : null;
  const errorId = error ? `${base}-error` : null;
  const described = [hintId, errorId, describedBy].filter(Boolean).join(' ') || null;

  // 자식이 컨트롤 하나일 때만 묶는다. 여럿이면 어느 것이 그 설명의 주인인지 알 수 없고,
  // 전부에 붙이면 같은 문장이 여러 번 읽힌다.
  const kids = toChildArray(children);
  const only = kids.length === 1 && kids[0] && typeof kids[0] === 'object' ? kids[0] : null;
  const body = described && only
    ? cloneElement(only, {
      'aria-describedby': [only.props?.['aria-describedby'], described].filter(Boolean).join(' '),
    })
    : children;

  return (
    <div class="field">
      <label class="field__label t-body-strong" for={htmlFor}>
        {label}
        {required ? <span class="field__req" aria-hidden="true"> *</span> : null}
      </label>
      {hint ? <p class="field__hint t-meta" id={hintId}>{hint}</p> : null}
      {body}
      {error ? (
        <p class="field__error t-meta" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export const Input = forwardRef(function Input({ size = 'md', invalid = false, ...rest }, ref) {
  return <input ref={ref} class={`ctl ctl--${size} input`} aria-invalid={invalid || undefined} {...rest} />;
});

/**
 * 여러 줄 입력.
 *
 * `autoGrow` 는 **기본값이 꺼짐**이다. 대화 작성창(`.composer`)은 자기 규칙으로
 * 높이를 다루고 있고, 여기서 전부 켜면 그 화면이 조용히 바뀐다. 켜는 자리는
 * 넘김·반환처럼 대화상자 안에서 제출 버튼이 밀려나는 곳뿐이다.
 */
export const Textarea = forwardRef(function Textarea(
  { rows = 3, invalid = false, autoGrow = false, maxRows = 12, onInput, value, ...rest },
  ref,
) {
  const own = useRef(null);
  const attach = useCallback((node) => {
    own.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  const grow = useCallback(() => {
    const element = own.current;
    if (!autoGrow || !element || typeof window === 'undefined') return;
    const styles = window.getComputedStyle(element);
    const lineHeight = Number.parseFloat(styles.lineHeight);
    const padding = Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom)
      + Number.parseFloat(styles.borderTopWidth) + Number.parseFloat(styles.borderBottomWidth);
    applyAutoGrow(element, {
      maxHeight: maxHeightForRows({
        rows: maxRows,
        lineHeight: Number.isFinite(lineHeight) ? lineHeight : 0,
        verticalPadding: Number.isFinite(padding) ? padding : 0,
      }),
    });
  }, [autoGrow, maxRows]);

  // 초안이 되살아나 값이 통째로 들어오는 경우가 있다(무효화 뒤 다시 mount). 그때도
  // 높이는 내용에 맞아야 한다 — 입력 사건만 보면 그 경우를 놓친다.
  useEffect(() => { grow(); }, [grow, value]);

  // Preact compat resets even an unchanged DOM node when `value` is present
  // but undefined. Uncontrolled drafts must have no value prop at all.
  return (
    <textarea
      ref={attach}
      rows={rows}
      class={`ctl ctl--md textarea${autoGrow ? ' textarea--grow' : ''}`}
      aria-invalid={invalid || undefined}
      {...(value === undefined ? {} : { value })}
      onInput={(event) => {
        grow();
        onInput?.(event);
      }}
      {...rest}
    />
  );
});

export const Select = forwardRef(function Select({ size = 'md', children, ...rest }, ref) {
  return (
    <span class="select">
      <select ref={ref} class={`ctl ctl--${size} select__el`} {...rest}>
        {children}
      </select>
      <Icon name="chevron-down" class="select__glyph" />
    </span>
  );
});

// ── Chip ──────────────────────────────────────────────────────────────────

export function Chip({ tone = 'neutral', children, ...rest }) {
  return (
    <span class={`chip chip--${tone} t-meta`} {...rest}>
      {children}
    </span>
  );
}

// ── Panel · List · Notice ─────────────────────────────────────────────────

/**
 * 모든 패널은 카드다. 그림자는 없고, 24px 열 여백 안에 16px 카드 여백을 중첩한다.
 */
export function Panel({ title, action, children, flush = false, ...rest }) {
  return (
    <section class={`panel${flush ? ' panel--flush' : ''}`} {...rest}>
      {title ? (
        <header class="panel__head">
          <h2 class="t-section">{title}</h2>
          {action ? <div class="panel__action">{action}</div> : null}
        </header>
      ) : null}
      <div class="panel__body">{children}</div>
    </section>
  );
}

// ── 작업면 머리 ───────────────────────────────────────────────────────────

/**
 * 작업면(자료·대화·연결…)의 첫 줄. **한 화면에 하나다.**
 *
 * 왜 컴포넌트가 됐나: 같은 머리를 화면마다 다시 그리다 보니 값이 갈렸다. 자료실 제목은
 * 14px, 대화 목록 제목은 16px, 버튼은 어떤 화면에서 12px 이고 다른 화면에서 14px 이었다.
 * 셋 다 「작업면의 h1」이라는 같은 역할인데 세 벌의 override 로 살아 있었다.
 *
 * 크기는 이제 여기 한 자리에서 나온다 — 제목 16 / 본문 14 / 메타 12, 버튼은 13px 글자에
 * 32px 높이다. **글자를 줄여도 누를 자리는 줄이지 않는다**(높이는 `--ctl-*` 가 정한다).
 *
 * @param actions 오른쪽에 붙는 행위. 쓸 수 없는 자리(보관된 Space 등)에서는 **주지 않는다** —
 *        눌러도 거절될 버튼을 그려 두지 않는다.
 */
export function PageHead({ eyebrow, title, meta, actions }) {
  return (
    <header class="work__head work__head--page">
      <div class="work__head-text">
        {eyebrow ? <p class="t-label page-head__eyebrow">{eyebrow}</p> : null}
        <h1 class="t-view page-head__title">{title}</h1>
        {meta ? <p class="t-meta page-head__meta">{meta}</p> : null}
      </div>
      {actions ? <div class="page-head__actions">{actions}</div> : null}
    </header>
  );
}

// ── Modal surface ─────────────────────────────────────────────────────────
//
// 지금 열려 있는 대화상자들. 맨 뒤가 위다. 모듈 수준에 있는 이유는 **겹쳐 열릴 수
// 있기 때문**이다 — 위의 하나만 Escape 를 받고, 배경 잠금은 그 하나를 기준으로 계산된다.
const dialogStack = [];
/** 우리가 잠근 요소 → 잠그기 전의 값. 되돌릴 수 없는 잠금은 만들지 않는다. */
const lockedBackground = new Map();
let priorBodyOverflow = null;

function lockBackground(element) {
  if (lockedBackground.has(element)) return;
  lockedBackground.set(element, {
    inert: element.hasAttribute('inert'),
    ariaHidden: element.getAttribute('aria-hidden'),
  });
  element.setAttribute('inert', '');
  element.setAttribute('aria-hidden', 'true');
}

function unlockBackground(element) {
  const prior = lockedBackground.get(element);
  if (!prior) return;
  lockedBackground.delete(element);
  if (!prior.inert) element.removeAttribute('inert');
  if (prior.ariaHidden === null) element.removeAttribute('aria-hidden');
  else element.setAttribute('aria-hidden', prior.ariaHidden);
}

/**
 * 배경을 잠그고 푼다. **스택에서 매번 다시 계산한다** — "열 때 잠그고 닫을 때 푼다"로
 * 두면 겹쳐 열렸다 안쪽만 닫혔을 때 바깥이 잠긴 채로 남는다.
 */
function applyDialogModality() {
  if (typeof document === 'undefined') return;
  const top = dialogStack[dialogStack.length - 1] ?? null;
  for (const child of [...document.body.children]) {
    if (top !== null && child !== top) lockBackground(child);
    else unlockBackground(child);
  }
  if (top === null) {
    for (const element of [...lockedBackground.keys()]) unlockBackground(element);
    if (priorBodyOverflow !== null) {
      document.body.style.overflow = priorBodyOverflow;
      priorBodyOverflow = null;
    }
    return;
  }
  if (priorBodyOverflow === null) {
    // 뒤 화면이 같이 스크롤되면 대화상자를 닫은 뒤 사람이 보던 자리가 사라진다.
    priorBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
}

/**
 * 현재 작업면을 떠나지 않는 짧은 결정·발급 흐름이다.
 *
 * 목록 아래에 폼을 이어 붙이면 사용자는 버튼이 아무 반응이 없었다고 느낀다. 반대로
 * 브라우저 새 페이지로 넘기면 Space·Node 맥락이 끊긴다. 그래서 키 발급, Scout, 폐기처럼
 * 현재 목록을 기준으로 끝나는 흐름은 이 한 겹의 modal surface에서만 연다.
 *
 * ## 이 대화상자가 지키는 것 (2026-09-12 검토에서 전부 빠져 있던 것들)
 *
 *   · **초점이 들어온다** — 열면 본문의 첫 컨트롤로 간다. 헤더의 「닫기」가 아니라
 *     사람이 하러 온 일이 있는 자리다.
 *   · **초점이 갇힌다** — Tab 은 마지막에서 처음으로, Shift+Tab 은 처음에서 마지막으로
 *     돈다. 가운데에서는 가로채지 않는다(브라우저 기본이 이미 맞다).
 *   · **초점이 돌아간다** — 닫으면 열었던 버튼으로. 안 그러면 키보드 사용자는 문서
 *     맨 위로 떨어진다.
 *   · **배경이 잠긴다** — `inert` + `aria-hidden`, 그리고 뒤 화면 스크롤 잠금.
 *   · **이름이 있다** — `aria-labelledby` 가 실제 제목 요소를 가리킨다.
 *
 * 초점 후보는 **누를 때마다 다시 모은다.** Preact 는 다시 그릴 때 노드를 갈아 끼우므로,
 * 열 때 한 번 모은 목록은 한 번의 리렌더로 죽은 노드를 가리킨다.
 *
 * @param onRequestClose 사람이 닫으려 할 때(Escape·배경·닫기) 대신 불린다. 아직 보내지
 *        않은 입력이 있는 폼이 「조용히 버리지 않기」를 직접 정할 수 있게 하는 자리다.
 *        주지 않으면 `onClose` 가 그대로 불린다.
 */
export function Dialog({ title, description, onClose, onRequestClose, initialFocusRef, children }) {
  const titleId = useId();
  const descriptionId = useId();
  const surfaceRef = useRef(null);
  const bodyRef = useRef(null);
  // **출처에 남는 표식.** portal 은 DOM 부모를 잃으므로, 이 자리에서 「내가 어디서 열렸나」를
  // 읽어 레이어에 옮긴다. `document.activeElement` 로 추측하지 않는 이유: 초점은 이미
  // 옮겨졌을 수 있고, 겹쳐 열린 경우 바깥 대화상자가 출처다.
  const anchorRef = useRef(null);
  const [layer] = useState(() => {
    if (typeof document === 'undefined') return null;
    const element = document.createElement('div');
    element.className = 'dialog-layer';
    return element;
  });

  const requestClose = useCallback((reason) => {
    if (typeof onRequestClose === 'function') onRequestClose(reason);
    else onClose?.();
  }, [onRequestClose, onClose]);
  // 아래 효과는 **한 번만** 돌아야 한다(열 때 초점, 닫을 때 되돌림). 그래서 매 렌더 새로
  // 만들어지는 닫기 함수를 의존성에 넣지 않고 ref 로 들고 본다.
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  // paint 전에 붙인다(`useLayoutEffect`). 뒤로 미루면 한 프레임 동안 테마 없는 레이어가
  // 그려지고, 그 한 프레임이 흰 번쩍임으로 보인다.
  useLayoutEffect(() => {
    if (!layer) return undefined;
    const opener = document.activeElement;
    // 출처의 테마를 그대로 들고 간다. 전역을 어둡게 만들지 않는다 — 밝은 화면(첫 진입·
    // 온보딩)에서 열린 대화상자는 계속 밝다.
    const themed = anchorRef.current?.closest?.('[data-theme]') ?? null;
    const theme = themed?.getAttribute('data-theme');
    if (theme) layer.setAttribute('data-theme', theme);
    else layer.removeAttribute('data-theme');
    document.body.appendChild(layer);
    dialogStack.push(layer);
    applyDialogModality();

    const target = initialFocus(
      focusableWithin(bodyRef.current),
      initialFocusRef?.current ?? null,
      surfaceRef.current,
    );
    target?.focus?.();

    // 초점이 어떤 경로로든 밖으로 나가면 데려온다(주소창을 거쳐 돌아오는 경우 등).
    const onFocusIn = (event) => {
      if (dialogStack[dialogStack.length - 1] !== layer) return;
      if (layer.contains(event.target)) return;
      const first = focusableWithin(surfaceRef.current)[0] ?? surfaceRef.current;
      first?.focus?.();
    };
    document.addEventListener('focusin', onFocusIn);

    // 초점이 대화상자 밖에 있는 짧은 순간(주소창을 거쳐 돌아오는 중 등)에도 Escape 는
    // 닫아야 한다. 안에서 온 Escape 는 surface 가 처리하므로 여기서는 밖의 것만 본다.
    const onEscapeOutside = (event) => {
      if (event.key !== 'Escape') return;
      if (dialogStack[dialogStack.length - 1] !== layer) return;
      if (layer.contains(event.target)) return;
      requestCloseRef.current('escape');
    };
    document.addEventListener('keydown', onEscapeOutside);

    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('keydown', onEscapeOutside);
      const at = dialogStack.indexOf(layer);
      if (at !== -1) dialogStack.splice(at, 1);
      layer.remove();
      applyDialogModality();
      // 열었던 자리로 돌려준다. 그 자리가 사라졌으면(목록이 다시 읽혔다) 아무것도 하지 않는다.
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, [layer]);

  if (!layer) return null;

  const onKeyDown = (event) => {
    if (dialogStack[dialogStack.length - 1] !== layer) return;
    if (event.key === 'Escape') {
      event.stopPropagation();
      requestClose('escape');
      return;
    }
    if (event.key !== 'Tab') return;
    const next = trapTarget(focusableWithin(surfaceRef.current), document.activeElement, event.shiftKey);
    if (!next) return;
    event.preventDefault();
    next.focus();
  };

  return <>
    {/* 출처 트리에 남는 표식 하나. 보이지 않고 초점을 받지 않는다. */}
    <span ref={anchorRef} class="dialog-anchor" hidden aria-hidden="true" />
    {createPortal(
      <div
        class="dialog-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) requestClose('backdrop');
        }}
      >
        <section
          ref={surfaceRef}
          class="dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descriptionId : undefined}
          tabindex="-1"
          onKeyDown={onKeyDown}
        >
          <header class="dialog__head">
            <h2 class="t-section" id={titleId}>{title}</h2>
            <Button size="sm" tone="ghost" onClick={() => requestClose('close-button')}>닫기</Button>
          </header>
          <div class="dialog__body" ref={bodyRef}>
            {description ? <p class="t-meta dialog__desc" id={descriptionId}>{description}</p> : null}
            {children}
          </div>
        </section>
      </div>,
      layer,
    )}
  </>;
}

export function List({ children, ...rest }) {
  return (
    <ul class="list" {...rest}>
      {children}
    </ul>
  );
}

export function ListRow({ marker = null, children, onActivate, href, ...rest }) {
  const interactive = typeof onActivate === 'function';
  return (
    <li class={`list__row${marker ? ` list__row--${marker}` : ''}`} {...rest}>
      {href ? (
        <Link class="list__hit" href={href}>
          {children}
        </Link>
      ) : interactive ? (
        <button type="button" class="list__hit" onClick={onActivate}>
          {children}
        </button>
      ) : (
        children
      )}
    </li>
  );
}

/**
 * 인라인 고지. **전체 화면 붉은 배너를 만들지 않는다**(§2.3).
 * `tone` 은 색만 정하고, 의미는 항상 문장이 전달한다.
 */
export function Notice({ tone = 'neutral', title, children, action }) {
  return (
    <div class={`notice notice--${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      <div class="notice__text">
        {title ? <p class="t-body-strong">{title}</p> : null}
        {children ? <div class="t-body notice__body">{children}</div> : null}
      </div>
      {action ? <div class="notice__action">{action}</div> : null}
    </div>
  );
}

// ── Icon ──────────────────────────────────────────────────────────────────

/**
 * 16×16 viewBox, stroke 1.5, fill none, currentColor.
 * **CSS 테두리·rotate(45deg) 로 글리프를 만들지 않는다.** 셰브론은 자산 하나이고
 * 회전은 0/90/180/270 네 값만 허용한다.
 */
const PATHS = {
  'chevron-down': 'M4 6.5 8 10.5 12 6.5',
  check: 'M3.5 8.5 6.5 11.5 12.5 4.5',
  plus: 'M8 3.5v9M3.5 8h9',
  copy: 'M5.5 5V3.5h7v8h-1.5M3.5 5.5h7v7h-7z',
  x: 'M4 4l8 8M12 4l-8 8',
  inbox: 'M2.5 9.5h3l1 2h3l1-2h3M2.5 9.5 4 3.5h8l1.5 6v3h-11z',
  thread: 'M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z',
  task: 'M3.5 8.5 6 11l6.5-6.5M2.5 2.5h11v11h-11z',
  node: 'M8 2.5 13.5 6v4L8 13.5 2.5 10V6z',
  arrow: 'M3 8h10M9 4l4 4-4 4',
  ember: 'M8 2.5c2 2.5 4.5 3.5 4.5 6.5a4.5 4.5 0 0 1-9 0c0-1.5.8-2.5 1.8-3.5.3 1 .9 1.6 1.7 1.8C6.6 5.8 7 4 8 2.5z',
  dormant: 'M12.5 9.6A5 5 0 0 1 6.4 3.5 5 5 0 1 0 12.5 9.6z',
  resource: 'M3 3.5h4l1 1.5h5v8H3z',
  space: 'M2.5 5.5h11M2.5 2.5h11v11h-11zM5.5 5.5v8',
};

/** 정체성 축 — 오브젝트마다 아이콘과 톤이 고정이다. 판단 색을 쓰지 않는다. */
export function IconChip({ object, name }) {
  return (
    <span class={`ichip ichip--${object}`}>
      <Icon name={name} />
    </span>
  );
}

/** 사진이 없다 — loom 에 사람 사진 개념이 없다. AI 는 색이 아니라 무채색으로 구분한다. */
export function Avatar({ label, ai = false }) {
  const initial = typeof label === 'string' && label.length > 0 ? label.slice(0, 1) : '?';
  return (
    <span class={`avatar${ai ? ' avatar--ai' : ''}`} aria-hidden="true">
      {initial}
    </span>
  );
}

export function Metrics({ children }) {
  return <div class="metrics">{children}</div>;
}

export function Metric({ label, value }) {
  return (
    <div class="metric">
      <p class="metric__label t-meta">{label}</p>
      <p class="metric__value t-lead">{value}</p>
    </div>
  );
}

export function GroupHead({ label, count }) {
  return (
    <div class="group-head">
      <span class="t-body-strong">{label}</span>
      {typeof count === 'number' ? <span class="t-meta t-num">{count}</span> : null}
    </div>
  );
}

/** 회전은 이 네 값만. 45°·225° 는 어긋난 화살표의 원인이라 **값 자체를 받지 않는다**. */
const TURNS = new Set([0, 90, 180, 270]);

export function Icon({ name, rotate = 0, ...rest }) {
  const d = PATHS[name];
  if (!d) return null;
  const turn = TURNS.has(rotate) ? rotate : 0;
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      focusable="false"
      style={turn ? `transform: rotate(${turn}deg); transform-origin: 50% 50%;` : undefined}
      {...rest}
    >
      <path d={d} />
    </svg>
  );
}

/**
 * liveness 글리프. **색이 아니라 형태다** — 채운 사각이 불씨, 빈 사각이 휴면.
 * 붉게 타오르는 표시는 재촉이고, 재촉은 §절대규칙 2 위반이다.
 */
export function EmberGlyph({ count = 0 }) {
  const live = count > 0;
  return (
    <span class={`ember${live ? '' : ' ember--dormant'}`}>
      <Icon name={live ? 'ember' : 'dormant'} />
      <span class="t-meta">{live ? count : '휴면'}</span>
      <span class="visually-hidden">{live ? `미결 약속 ${count}건` : '휴면 — 정상 상태입니다'}</span>
    </span>
  );
}
